import styled from '@emotion/styled';

import { MAX_CRUMB_LENGTH } from '@/common/categories';
import { Link } from '@/common/links';
import Tooltip from '@/components/common/Tooltip';

export type TCrumb = {
  name: string;
  id: string;
  url?: string;
};

const StyledContainer = styled.div`
  font-size: ${(props) => props.theme.fontSizeBase};
  line-height: ${(props) => props.theme.lineHeightMd};
  margin-bottom: ${(props) => props.theme.spaces.s100};

  ${(props) => props.theme.breakpoints.up('md')} {
    font-size: ${(props) => props.theme.fontSizeMd};
  }
`;

function Crumb({ crumb, sibling }: { crumb: TCrumb; sibling?: boolean }) {
  const id = `crumb-${crumb.id}`;
  const isTruncated = crumb.name.length > MAX_CRUMB_LENGTH;
  const name = isTruncated ? `${crumb.name.slice(0, MAX_CRUMB_LENGTH).trim()}...` : crumb.name;

  const isSeparator = crumb.name === '';
  if (isSeparator) {
    return (
      <>
        &nbsp;/
        <br />
      </>
    );
  }
  const crumbContent = (
    <span id={id}>
      {crumb.url ? (
        <Link href={crumb.url} passHref>
          {name}
        </Link>
      ) : (
        name
      )}
      {!sibling && <>&nbsp;/ </>}
    </span>
  );

  if (!isTruncated) return crumbContent;

  return (
    <Tooltip title={crumb.name} describeChild>
      {crumbContent}
    </Tooltip>
  );
}

export default function Breadcrumbs({ breadcrumbs }: { breadcrumbs: TCrumb[] | null | undefined }) {
  // Try to handle duplicate breadcrumbs due to multiselect categories
  // this might not work for all cases
  if (!breadcrumbs) return null;
  const uniqueBreadcrumbs = breadcrumbs.reduce((acc, current) => {
    const index = acc.findIndex((item) => item.id === current.id);
    if (index >= 0) {
      acc.push({ id: `${current.id}-separator`, name: '' });
      acc.push(current);
    } else {
      // Keep the first instance
      acc.push(current);
    }
    return acc;
  }, [] as TCrumb[]);

  return (
    <StyledContainer>
      {uniqueBreadcrumbs.map((crumb, index) => (
        <Crumb key={crumb.id} crumb={crumb} sibling={uniqueBreadcrumbs[index + 1]?.name === ''} />
      ))}
    </StyledContainer>
  );
}
